import { useCallback } from 'react';
import { UnitStatusSearchForm, UnitStatusSearchResponse } from '@mark43/cad-api';
import { ResultsTab } from '../search-windows/types';
import { useSearchUnitStatusesQuery } from './use-search-unit-statuses-query';
import { useExecuteSavedUnitStatusSearchQuery } from './use-execute-saved-unit-status-search-query';
import { useUnitStatusSearchTabsContext } from './unit-status-search-tabs-provider';
import {
    UnitStatusSearchResultsPaginationProvider,
    useUnitStatusSearchResultsPaginationContext,
} from './unit-status-search-results-pagination-provider';
import { SearchResultsTab } from '../search-windows/search-results-tab';
import { useExportUnitStatusSearchResultsMutation } from './use-export-unit-status-search-results-mutation';
import { useObservableSearchResultSelectionStore } from '../search-windows/use-observable-search-results-selection-store';
import { UnitStatusSearchResultsTable } from './unit-status-search-results-table';
import { observer } from 'mobx-react-lite';
import { UnitStatusSearchResultsHeader } from './unit-status-search-results-header';
import { UnitStatusSearchSortProvider } from './unit-status-search-sort-provider';
import { FileExportType } from '../event-search/types';

export const _UnitStatusSearchResultsTab = observer(function _UnitStatusSearchResultsTab({
    tab,
}: Readonly<{ tab: ResultsTab<UnitStatusSearchForm> }>) {
    const tabsValue = useUnitStatusSearchTabsContext();
    const pagination = useUnitStatusSearchResultsPaginationContext();
    const searchUnitStatusesQuery = useSearchUnitStatusesQuery(
        tab.id,
        tab.search,
        tab.savedSearchId
    );
    const executeSavedSearchQuery = useExecuteSavedUnitStatusSearchQuery(tab.id, tab.savedSearchId);
    const exportUnitStatusSearchResultsMutation = useExportUnitStatusSearchResultsMutation();
    const unitStatusSearchResponse = searchUnitStatusesQuery.data ?? executeSavedSearchQuery.data;
    const totalCount = unitStatusSearchResponse?.totalCount ?? 0;
    const results = (
        unitStatusSearchResponse as UnitStatusSearchResponse
    )?.unitStatusSearchRows.map((row) => ({
        id: row.unitStatusRmsEventId,
    }));
    const selection = useObservableSearchResultSelectionStore(results ?? [], totalCount);

    const handleFileExport = useCallback(
        ({ entityIds }: { type?: FileExportType; entityIds?: number[] }) => {
            exportUnitStatusSearchResultsMutation.mutate({
                unitStatusRmsEventIds: entityIds ?? [],
                unitStatusSearchForm: entityIds?.length ? undefined : tab.search,
            });
        },
        [exportUnitStatusSearchResultsMutation, tab.search]
    );

    return (
        <SearchResultsTab
            tab={tab}
            results={results}
            totalCount={totalCount}
            onClose={() => tabsValue.deleteTab(tab.title)}
            pagination={pagination}
            selection={selection}
            searchQuery={searchUnitStatusesQuery}
            executeSavedSearchQuery={executeSavedSearchQuery}
            onFileExport={handleFileExport}
            isExportLoading={exportUnitStatusSearchResultsMutation.isLoading}
            header={
                <UnitStatusSearchResultsHeader
                    hasResults={results?.length > 0}
                    unitStatusSearchForm={tab.search}
                    savedSearchId={tab.savedSearchId}
                    setSavedSearchId={tab.setSavedSearchId}
                />
            }
        >
            <UnitStatusSearchResultsTable
                unitStatusSearchResponse={unitStatusSearchResponse}
                selection={selection}
            />
        </SearchResultsTab>
    );
});

export function UnitStatusSearchResultsTab({ tab }: { tab: ResultsTab<UnitStatusSearchForm> }) {
    return (
        <UnitStatusSearchResultsPaginationProvider>
            <UnitStatusSearchSortProvider>
                <_UnitStatusSearchResultsTab tab={tab} />
            </UnitStatusSearchSortProvider>
        </UnitStatusSearchResultsPaginationProvider>
    );
}
